// [2026-03-22] 역할 기반 메뉴 필터링 + 테넌트 전환
import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usersAPI } from '../services/api';

// 메뉴 항목 — superOnly: 슈퍼관리자 전용, adminOnly: admin 이상
const navItems = [
  { path: '/', label: '대시보드', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { path: '/tenants', label: '테넌트 관리', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', superOnly: true },
  { path: '/users', label: '사용자 관리', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', superOnly: true },
  { path: '/documents', label: '문서 관리', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  { path: '/conversations', label: '대화 내역', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
  { path: '/chat-test', label: '채팅 테스트', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
  { path: '/settings', label: '설정', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z', adminOnly: true },
];

const monitoringItems = [
  { path: '/monitoring', label: '전체 현황', end: true },
  { path: '/monitoring/tokens', label: '토큰/비용' },
  { path: '/monitoring/tenants', label: '테넌트별 사용량', superOnly: true },
  { path: '/monitoring/logs', label: '대화 로그' },
];

const externalLinks = [
  { url: '/sample-chatbot.html', label: '위젯 데모', icon: 'M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14' },
];

export function DashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, activeTenant, setActiveTenant } = useAuthStore();
  const [monitoringOpen, setMonitoringOpen] = useState(location.pathname.startsWith('/monitoring'));
  const isSuperuser = user?.is_superuser === true;
  const userRole = activeTenant?.role || (isSuperuser ? 'admin' : 'viewer');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchTenant = async (tenantId: string) => {
    const tenant = user?.tenants?.find(t => t.tenant_id === tenantId);
    if (!tenant) return;
    try {
      const res = await usersAPI.switchTenant(tenantId);
      localStorage.setItem('access_token', res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      setActiveTenant(tenant);
      window.location.reload();
    } catch {
      // 실패 시 무시
    }
  };

  // 역할별 메뉴 필터링
  const filteredNavItems = navItems.filter(item => {
    if ((item as any).superOnly && !isSuperuser) return false;
    if ((item as any).adminOnly && userRole === 'viewer') return false;
    return true;
  });

  const filteredMonitoringItems = monitoringItems.filter(item => {
    if ((item as any).superOnly && !isSuperuser) return false;
    return true;
  });

  // admin 이상만 모니터링 표시
  const showMonitoring = isSuperuser || userRole === 'admin';

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col">
        <div className="p-4 border-b border-gray-700">
          <h1 className="text-xl font-bold">AI Chatbot</h1>
          <p className="text-sm text-gray-400">
            {isSuperuser ? '관리자 대시보드' : activeTenant?.tenant_name || '대시보드'}
          </p>
        </div>

        {/* 테넌트 전환 (다중 테넌트 사용자만) */}
        {!isSuperuser && user?.tenants && user.tenants.length > 1 && (
          <div className="px-4 pt-3 pb-1">
            <select
              value={activeTenant?.tenant_id || ''}
              onChange={(e) => handleSwitchTenant(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-800 border border-gray-600 rounded text-sm text-white"
            >
              {user.tenants.map(t => (
                <option key={t.tenant_id} value={t.tenant_id}>{t.tenant_name}</option>
              ))}
            </select>
          </div>
        )}

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {filteredNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 rounded-lg transition ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-300 hover:bg-gray-800'
                    }`
                  }
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={item.icon}
                    />
                  </svg>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Monitoring — admin 이상만 */}
          {showMonitoring && (
            <div className="mt-4 pt-4 border-t border-gray-700">
              <button
                onClick={() => setMonitoringOpen(!monitoringOpen)}
                className={`flex items-center justify-between w-full px-4 py-2 rounded-lg transition ${
                  location.pathname.startsWith('/monitoring')
                    ? 'text-white bg-gray-800'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  모니터링
                </div>
                <svg className={`w-4 h-4 transition-transform ${monitoringOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {monitoringOpen && (
                <ul className="mt-1 ml-8 space-y-1">
                  {filteredMonitoringItems.map((item) => (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.end}
                        className={({ isActive }) =>
                          `block px-3 py-1.5 rounded text-sm transition ${
                            isActive
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-800'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* External Links */}
          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="px-4 text-xs text-gray-500 uppercase tracking-wider mb-2">외부 링크</p>
            <ul className="space-y-2">
              {externalLinks.map((link) => (
                <li key={link.url}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2 rounded-lg transition text-gray-300 hover:bg-gray-800"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d={link.icon}
                      />
                    </svg>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <div className="p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
              <span className="text-lg font-medium">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-sm text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition"
          >
            로그아웃
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
